import { useMemo } from "react";
import { useSelector } from "react-redux";

import { ClearingSaleBuyerStatus } from "constants/clearingSaleAttributes";
import { GlobalSearchFields } from "constants/globalSearch";
import { PaymentMethod } from "constants/payments";

import { EMPTY_ARRAY } from "lib";
import { isWholePositiveNumber } from "lib/math";

import {
  getGlobalSearchBySearchField,
  getKeywordLookups,
  selectAlphabeticalBuyerIds,
  selectBidderNumbersByBuyerIdLookup,
  selectBusinessIdByRegistrationNumberLookup,
  selectBuyerStatusByBuyerIdLookup,
  selectPaymentMethodsByBuyerIdLookup,
} from "selectors";

import {
  getBuyerFilter,
  getPaymentMethodFilter,
  getStatusFilter,
} from "./buyerFilterPolicy";
import {
  getSortComparator,
  type SortField,
  type ValuesByBuyerId,
} from "./buyerSortPolicy";

export function useSortedBuyerIds(
  searchText: string,
  sortBy: SortField,
  isUsingRegisteredBidders: boolean,
) {
  const buyerIds = useSelector(selectAlphabeticalBuyerIds);
  const businessKeywordLookup = useSelector(getKeywordLookups).businesses;
  const businessIdByRegistrationNumberLookup = useSelector(
    selectBusinessIdByRegistrationNumberLookup as (
      state: RootState,
    ) => Record<string, string>,
  );
  const buyerStatusByBuyerId = useSelector(
    selectBuyerStatusByBuyerIdLookup as (
      state: RootState,
    ) => Record<string, ClearingSaleBuyerStatus>,
  );
  const paymentMethodsByBuyerId = useSelector(
    selectPaymentMethodsByBuyerIdLookup as (
      state: RootState,
    ) => Record<string, PaymentMethod[]>,
  );
  const bidderNumbersByBuyerId = useSelector(
    selectBidderNumbersByBuyerIdLookup as (state: RootState) => ValuesByBuyerId,
  );

  const statusFilter = useSelector(
    getGlobalSearchBySearchField(GlobalSearchFields.BuyerStatus) as (
      state: RootState,
    ) => ClearingSaleBuyerStatus[] | null,
  );
  const paymentMethodFilter = useSelector(
    getGlobalSearchBySearchField(GlobalSearchFields.PaymentMethod) as (
      state: RootState,
    ) => PaymentMethod[] | null,
  );
  const buyerFilter = useSelector(
    getGlobalSearchBySearchField(GlobalSearchFields.Buyer) as (
      state: RootState,
    ) => string[] | null,
  );

  const textMatchedIds = useMemo(() => {
    const trimmedSearchText = searchText.trim();

    if (isUsingRegisteredBidders && isWholePositiveNumber(trimmedSearchText)) {
      return buyerIds.filter(
        businessId =>
          businessId ===
          businessIdByRegistrationNumberLookup[trimmedSearchText],
      );
    }

    const lowerSearchText = trimmedSearchText.toLowerCase();
    const matchingBusinessIdsLookup = Object.keys(
      businessKeywordLookup,
    ).reduce<Record<string, boolean>>((acc, lookupToken) => {
      if (lookupToken.includes(lowerSearchText)) {
        businessKeywordLookup[lookupToken].forEach(b => {
          acc[b.business.id] = true;
        });
      }
      return acc;
    }, {});

    return buyerIds.filter(id => matchingBusinessIdsLookup[id]);
  }, [
    isUsingRegisteredBidders,
    searchText,
    buyerIds,
    businessIdByRegistrationNumberLookup,
    businessKeywordLookup,
  ]);

  return useMemo(() => {
    const statusFilterFn = getStatusFilter(statusFilter ?? EMPTY_ARRAY);
    const paymentMethodFilterFn = getPaymentMethodFilter(
      paymentMethodFilter ?? EMPTY_ARRAY,
    );
    const buyerFilterFn = getBuyerFilter(buyerFilter ?? EMPTY_ARRAY);

    const filteredIds = textMatchedIds.filter(
      id =>
        statusFilterFn(buyerStatusByBuyerId[id]) &&
        paymentMethodFilterFn(paymentMethodsByBuyerId[id] ?? EMPTY_ARRAY) &&
        buyerFilterFn(id),
    );

    const comparator = getSortComparator(
      sortBy,
      { buyerStatusByBuyerId, bidderNumbersByBuyerId },
      filteredIds,
    );

    return comparator ? [...filteredIds].sort(comparator) : filteredIds;
  }, [
    textMatchedIds,
    statusFilter,
    paymentMethodFilter,
    buyerFilter,
    paymentMethodsByBuyerId,
    sortBy,
    buyerStatusByBuyerId,
    bidderNumbersByBuyerId,
  ]);
}
